import React from "react";
import { useSeo } from "../lib/useSeo";
import { PublicPageHeader, PublicPageFooter } from "./PublicPageChrome";

const MecklenburgTaxForeclosures: React.FC = () => {
  useSeo({
    title: "Mecklenburg County Tax Foreclosures & the NC Upset Bid Process | Parcely",
    description:
      "How tax foreclosures work in Mecklenburg County, NC: how a parcel goes to auction, how the 10-day upset bid window works, and where to find current cases. A plain-English guide for investors.",
  });

  return (
    <>
      <PublicPageHeader />
      <div className="legal-page" id="main" tabIndex={-1}>
        <div className="legal-content">
          <h1>Tax Foreclosures in Mecklenburg County, NC</h1>
          <p className="legal-updated">A plain-English guide for buyers and investors</p>

          <p>
            When a property owner in Mecklenburg County stops paying property taxes,
            the county can take the property to a public auction and sell it to recover
            what is owed. That is a tax foreclosure. It runs on a different track from a
            bank (mortgage) foreclosure, and the rules that make it interesting to
            investors are the upset bid rules described below.
          </p>

          <p>
            This guide covers the basics: how a parcel gets to auction, what the 10-day
            upset bid window is, and where to find cases that are open right now. None of
            this is legal or financial advice. Verify anything you act on against the
            county and court records.
          </p>

          <h2>How a tax foreclosure starts</h2>
          <p>
            Property taxes in North Carolina become delinquent after January 6 of the
            year after they are due. Interest starts adding up that day. The county tax
            collector sends notices, and unpaid accounts pile up over months or years.
          </p>
          <p>
            At that point the tax bill is just a debt. There is no court case yet, no
            auction date, and nothing public to track beyond the delinquency itself. A
            parcel can sit in this stage for a long time before anything happens.
          </p>

          <h2>From delinquent tax bill to a court case</h2>
          <p>
            To actually sell the property, the county (or a law firm it hires) files a
            foreclosure action in Superior Court. North Carolina allows two methods:
          </p>
          <p>
            <strong>Mortgage-style foreclosure</strong> under{" "}
            <abbr title="North Carolina General Statutes">N.C.G.S.</abbr> §105-374. The
            county files a civil lawsuit, the court enters a judgment, and a commissioner
            sells the property at a public sale. This is the method that goes through the
            full court process and produces a case number you can look up.
          </p>
          <p>
            <strong>In rem foreclosure</strong> under N.C.G.S. §105-375. The county
            (often through a firm such as Kania Law) dockets a judgment against the
            property directly, without a full lawsuit. It is faster and cheaper for the
            county, and it still ends in a public sale.
          </p>
          <p>
            The case number is created when the action is <em>filed</em>, not when the
            taxes first go unpaid. That gap matters: a parcel can be deeply delinquent
            for years with no case number, because no one has filed yet.
          </p>

          <h2>The auction</h2>
          <p>
            Once there is a judgment, the property is sold at a public auction, usually at
            the Mecklenburg County courthouse. The highest bidder at the sale does not
            own the property yet. They have placed the high bid, and the sale gets
            reported to the court. Then the clock that defines this whole process starts.
          </p>

          <h2>The 10-day upset bid window</h2>
          <p>
            This is the part that makes NC tax sales different from a typical
            courthouse-steps auction. After the sale is reported, North Carolina law
            (N.C.G.S. §1-339.25) opens a <strong>10-day window</strong> where anyone can
            raise the bid. Beat the standing bid by the greater of <strong>5% or
            $750</strong>, put up the required deposit, and you become the new high
            bidder.
          </p>
          <p>
            Each qualifying upset bid <strong>restarts the 10-day clock</strong>. So a
            property can stay open for weeks if bidders keep raising each other. The sale
            only becomes final when a full 10 days pass with no new upset bid. After that
            the court confirms the sale and the deed is delivered.
          </p>
          <p>
            A real example from our data: one Charlotte parcel opened around $11,000 and
            climbed past $48,000 across a string of upset bids before it settled. The
            opening number at the auction is often nowhere near where these land.
          </p>

          <h2>Why investors watch the upset window</h2>
          <p>
            The upset period is the part of the process you can actually act on. The
            auction itself is one moment on one morning. The 10-day window is days of
            public, open competition where a new bidder can step in. If you know which
            cases have an open window and how much room is left, you know where to look.
          </p>
          <p>
            The same window cuts the other way. A property that closed weeks ago is done,
            even if it still shows up on a stale list somewhere. Knowing the difference
            between an open window and a closed sale is most of the work.
          </p>

          <h2>How to find cases and place a bid</h2>
          <p>
            These sales run through the county courthouse. To find active cases or place
            an upset bid, you contact the Mecklenburg County courthouse — the Clerk of
            Superior Court's office holds the sale file and takes upset bids. You post the
            required deposit there during the 10-day window.
          </p>

          <h2>How Parcely helps</h2>
          <p>
            Parcely pulls these public sources into one place for North Carolina tax
            foreclosures. You can see active cases on a map, filter to properties with an
            open upset bid window, and watch the bid history on a parcel instead of
            checking the courthouse file by hand. We track Mecklenburg and are expanding
            across the Charlotte metro.
          </p>
          <p>
            <a
              href="/public/mecklenburg-nc"
              className="btn-primary"
              style={{ display: "inline-block", textDecoration: "none", marginTop: "0.5rem" }}
            >
              See Mecklenburg foreclosure data →
            </a>
          </p>

          <hr className="legal-divider" />
          <p>
            <strong>This is general information, not legal or financial advice.</strong>{" "}
            Tax foreclosure rules, deposit amounts, and deadlines change, and every case
            has its own facts. Confirm anything you plan to act on with the Mecklenburg
            County tax office, the Clerk of Superior Court, and your own attorney before
            you bid.
          </p>
          <p className="legal-entity">
            <strong>Parcely LLC</strong>
            <br />
            Charlotte, NC
          </p>
        </div>
      </div>
      <PublicPageFooter />
    </>
  );
};

export default MecklenburgTaxForeclosures;
